<template>
  <div class="country-lang-manager">
    <!-- 新增国家按钮和同步按钮 -->
    <div class="add-country-btn">
      <el-button type="primary" size="small" @click="handleAddCountry">
        <i class="el-icon-plus"></i>
        {{ $t('新增国家') }}
      </el-button>
      <el-button 
        type="success" 
        size="small" 
        icon="el-icon-refresh" 
        @click="handleSyncLanguages"
        :disabled="!currentSelected.code"
        style="margin-left: 8px;">
        {{ $t('同步语言') }}
      </el-button>
    </div>

    <!-- 国家列表 -->
    <div class="country-list">
      <el-tree
        :data="countryTreeData"
        :props="treeProps"
        :expand-on-click-node="false"
        :default-expand-all="true"
        node-key="id"
        ref="countryTree"
        class="country-tree">

        <template #default="{ data }">
          <div
            class="tree-node"
            :class="{
              'node-selected': selectedNodeId === data.id,
              'parent-selected': isParentSelected(data),
              'level1-disabled': data.type === 'country',
              'level2-selectable': data.type === 'language'
            }"
            @click="handleNodeClick(data)">
            <div class="node-content">
              <span class="node-label">{{ data.label }}</span>
              <span class="node-level" v-if="data.level">({{ data.level }})</span>
            </div>
            <div class="node-actions" v-if="data.type === 'country'">
              <i
                class="el-icon-edit"
                style="color: #409eff"
                @click.stop="handleEditCountry(data)">
              </i>
              <i
                class="el-icon-delete"
                style="color: #f56c6c"
                @click.stop="handleDelete(data)">
              </i>
            </div>
          </div>
        </template>
      </el-tree>
    </div>

    <!-- 新增/编辑国家弹窗 -->
    <el-dialog
      :title="isEditMode ? $t('编辑国家') : $t('新增国家')"
      :visible.sync="addCountryDialogVisible"
      width="450px"
      :close-on-click-modal="false"
      class="country-dialog">

      <div class="dialog-content">
        <el-form :model="countryForm" :rules="countryRules" ref="countryForm" label-width="80px">
          <!-- 多语言输入区域 -->
          <div class="multilang-input-section">
            <MultiLangInput
              v-model="countryForm.languages"
              :language-list="langs"
              :label="$t('国家')"
              :placeholder="$t('选择语言')"
              :input-placeholder="$t('请输入国家名称')"
              :default-language="'zh'"
              :show-status="true">
            </MultiLangInput>
          </div>
        </el-form>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="handleCancelAddCountry">{{ $t('取消') }}</el-button>
          <el-button type="primary" @click="handleConfirmAddCountry">{{ $t('确定') }}</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import MultiLangInput from '../MultiLangInput.vue'
import { request } from '@/config/request'
import api from '@/config/api'
import {handleDeleteTip, handleDeleteTreeNode} from "@/utils/levelUtils";

export default {
  name: 'CountryLangManager',
  components: {
    MultiLangInput
  },
  data() {
    return {
      addCountryDialogVisible: false,
      isEditMode: false, // 是否为编辑模式
      selectedNodeId: null, // 当前选中的节点ID
      countryForm: {
        code: '', // 编辑时使用
        languages: {}
      },
      countryRules: {},
      treeProps: {
        children: 'children',
        label: 'label'
      },
      countryTreeData: [],
      defaultLang: "zh-Hans",
      currentSelected: {
        code: '', // 当前选中的国家code
        language: '', // 当前选中的语言code
        name: '' // 当前选中的语言名称
      },
    }
  },
  methods: {
    // 获取区域树数据
    async fetchRegionTree() {
      try {
        const data = await request(api.region.tree, { level: 1 })
        this.countryTreeData = this.transformRegionData(data.tree || [])

        // 自动选中第一条数据的默认语言
        this.autoSelectDefault()
      } catch (error) {
        console.error(this.$t('获取区域数据失败:'), error)
        this.$message.error(this.$t('获取区域数据失败'))
      }
    },

    // 转换API数据为组件需要的格式
    transformRegionData(regionList) {
      return regionList.map((region) => {
        // 获取默认语言显示的名称
        const defaultName = this.getDefaultLanguageName(region.names)

        // 创建语言子节点，统一使用langs
        const languageChildren = this.langs.map((lang) => ({
          id: `${region.code}_${lang.code}`,
          type: 'language',
          label: lang.name,
          level: '2级',
          languageCode: lang.code,
          parentCode: region.code
        }))

        return {
          id: region.code,
          type: 'country',
          label: defaultName,
          level: '1级',
          expanded: false,
          code: region.code,
          parentCode: region.parentCode,
          sort: region.sort,
          refGeo: region.refGeo,
          names: region.names,
          children: languageChildren
        }
      })
    },

    // 获取默认语言显示的名称
    getDefaultLanguageName(names) {
      if (!names) return ''

      // 优先使用defaultLang
      if (names[this.defaultLang]) {
        return names[this.defaultLang]
      }

      // 如果没有defaultLang，则使用第一个可用的名称
      const availableNames = Object.values(names).filter(name => name && name.trim())
      return availableNames[0] || ''
    },

    // 自动选中默认语言
    autoSelectDefault() {
      if (this.countryTreeData.length === 0) return

      const firstCountry = this.countryTreeData[0]
      if (!firstCountry || !firstCountry.children || firstCountry.children.length === 0) return

      // 查找默认语言对应的子节点
      const defaultLanguageNode = firstCountry.children.find(child =>
        child.languageCode === this.defaultLang
      )

      if (defaultLanguageNode) {
        // 选中默认语言节点
        this.selectedNodeId = defaultLanguageNode.id
        this.updateCurrentSelected(defaultLanguageNode)
      } else {
        // 如果没有找到默认语言，选中第一个语言节点
        const firstLanguageNode = firstCountry.children[0]
        this.selectedNodeId = firstLanguageNode.id
        this.updateCurrentSelected(firstLanguageNode)
      }
    },

    // 更新当前选中信息
    updateCurrentSelected(languageNode) {
      this.currentSelected = {
        code: languageNode.parentCode,
        language: languageNode.languageCode,
        name: languageNode.label
      }

      // 触发事件，通知父组件
      this.$emit('selection-change', this.currentSelected)
    },

    // 处理节点点击
    handleNodeClick(data) {
      // 只有2级（语言）节点才能被选中
      if (data.type === 'language') {
        this.selectedNodeId = data.id
        this.updateCurrentSelected(data)
      }
    },

    // 判断父级是否被选中
    isParentSelected(data) {
      if (data.type === 'country') {
        // 检查是否有子节点被选中
        return data.children && data.children.some(child => this.selectedNodeId === child.id)
      }
      return false
    },

    // 新增国家
    handleAddCountry() {
      this.addCountryDialogVisible = true
      this.resetCountryForm()
    },

    // 重置表单
    resetCountryForm() {
      this.countryForm = {
        code: '',
        languages: {}
      }
      this.isEditMode = false
    },

    // 确认新增/编辑国家
    async handleConfirmAddCountry() {
      // 验证是否有输入内容
      const hasContent = Object.values(this.countryForm.languages).some(name => name && name.trim())
      if (!hasContent) {
        this.$message.warning(this.$t('请输入国家名称'))
        return
      }

      try {
        // 准备API请求参数
        const names = {}
        Object.entries(this.countryForm.languages).forEach(([code, name]) => {
          if (name && name.trim()) {
            names[code] = name.trim()
          }
        })

        console.log('names', names);

        // 根据模式调用不同的API接口
        if (this.isEditMode) {
          // 编辑模式
          await request(api.region.update, {
            code: this.countryForm.code,
            level: 1,
            names: names,
            sort: this.countryTreeData.find(item => item.code === this.countryForm.code)?.sort || 1,
          })
          this.$message.success(this.$t('编辑国家成功'))
        } else {
          // 新增模式
          await request(api.region.add, {
            level: 1,
            names: names,
            sort: this.countryTreeData.length + 1,
          })
          this.$message.success(this.$t('新增国家成功'))
        }

        // 重新获取数据
        await this.fetchRegionTree()
        this.addCountryDialogVisible = false
      } catch (error) {
        console.error(this.isEditMode ? this.$t('编辑国家失败:') : this.$t('新增国家失败:'), error)
        this.$message.error(this.isEditMode ? this.$t('编辑国家失败') : this.$t('新增国家失败'))
      }
    },

    // 取消新增国家
    handleCancelAddCountry() {
      this.addCountryDialogVisible = false
      this.resetCountryForm()
    },

    // 编辑国家
    handleEditCountry(data) {
      this.isEditMode = true
      this.addCountryDialogVisible = true

      // 设置编辑表单数据
      this.countryForm.code = data.code
      this.countryForm.languages = { ...data.names }
    },

    // 删除国家
    async handleDelete(row) {
      try {
        await handleDeleteTip();
        await request(this.$api.region.delete, {code: row.code});
        this.$message.success(this.$t('操作成功'));
        this.fetchRegionTree();
      } catch (e) {
        console.error(e);
      }
    },

    // 获取当前选中的信息
    getCurrentSelection() {
      return this.currentSelected
    },

    // 处理语言同步
    async handleSyncLanguages() {
      try {
        // 检查是否有选中的国家
        if (!this.currentSelected.code) {
          this.$message.warning(this.$t('请先选择一个国家/语言'));
          return;
        }

        // 显示确认对话框
        const confirmed = await this.showSyncConfirmDialog();
        if (!confirmed) {
          return;
        }

        // 触发同步事件，通知父组件
        this.$emit('sync-languages');
      } catch (error) {
        console.error('同步语言失败:', error);
        this.$message.error(this.$t('同步失败'));
      }
    },

    // 显示同步确认对话框
    showSyncConfirmDialog() {
      return new Promise((resolve) => {
        this.$confirm(
          this.$t('确定要将有内容的语言同步到其他空语言吗？'),
          this.$t('同步确认'),
          {
            confirmButtonText: this.$t('确定'),
            cancelButtonText: this.$t('取消'),
            type: 'info'
          }
        ).then(() => {
          resolve(true);
        }).catch(() => {
          resolve(false);
        });
      });
    }
  },
  mounted() {
    this.fetchRegionTree()
  },
  computed: {
    langs() {
      const list = this.$store.getters.getDictByFlag('LANGUAGE-VERSIONFRONT');
      return list.map(item => {
        return {
          code: item.dictValue,
          name: item.name + `（${item.data}）`,
        }
      })
    }
  }
}
</script>

<style scoped>
.country-lang-manager {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 20px;
  box-sizing: border-box;
}

.add-country-btn {
  padding-bottom: 16px;
  border-bottom: 1px solid #e6e6e6;
  margin-bottom: 16px;
}

.country-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px 0; /* 增加上下内边距，确保激活样式完整显示 */
  margin: -4px 0; /* 负边距抵消部分padding，保持整体布局 */
}

.country-tree {
  border: none;
}

.tree-node {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 4px 8px; /* 减少内边距，降低高度 */
  border-radius: 4px;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
}

/* 2级节点选中样式 */
.tree-node.node-selected {
  background-color: #409eff;
  color: #fff;
}

.tree-node.node-selected .node-label,
.tree-node.node-selected .node-level {
  color: #fff;
}

/* 父级1级节点激活样式 */
.tree-node.parent-selected {
  background-color: #e6f7ff;
  border: 1px solid #91d5ff;
}

.tree-node.parent-selected .node-label,
.tree-node.parent-selected .node-level {
  color: #1890ff;
  font-weight: 500;
}


/* 统一的hover样式 */
.tree-node.level2-selectable:hover {
  color: #ffffff;
  background: #1890ff;
}

.tree-node.level2-selectable:hover .node-label,
.tree-node.level2-selectable:hover .node-level {
  color: #ffffff;
}

.node-content {
  display: flex;
  align-items: center;
  flex: 1;
}

.node-label {
  font-size: 14px;
}

.node-level {
  font-size: 12px;
  margin-left: 4px;
}

.node-actions {
  display: flex;
  align-items: center;
  column-gap: 10px;
}

/* 树节点样式调整 */
::v-deep .el-tree-node__content {
  height: 28px; /* 降低高度 */
  line-height: 28px;
  border-radius: 4px;
  transition: all 0.2s ease;
  margin: 8px 0;
  padding-left: 0 !important;
}

::v-deep .el-tree-node__content:hover, .el-upload-list__item:hover {
  background: #ffffff;
}

::v-deep .el-tree-node__expand-icon.is-leaf {
  display: none;
}

::v-deep .el-tree-node__children {
  padding-left: 32px;
}

/* 弹窗样式 */
.country-dialog .el-dialog__body {
  padding: 20px;
}

.dialog-content .multilang-input-section {
  margin-bottom: 16px;
}

.dialog-footer {
  text-align: right;
  padding-top: 10px;
  border-top: 1px solid #e6e6e6;
}
</style>
